import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

/**
 * P9 — background search awareness.
 *
 * Owns the in-progress search (id, running flag, progress text, error) OUTSIDE the Dashboard,
 * so it survives navigation between authenticated routes. Mounted around AppLayout, which is
 * itself a layout route element and therefore stays mounted across child route changes.
 *
 * Polling contract (unchanged from the previous Dashboard-local implementation):
 *   - hits GET /searches/{id}/status ONLY, every 3000 ms
 *   - NEVER hits GET /searches as a poll target (it is an N+1 returning every lead)
 *   - stops on "completed" and "failed"
 *   - network errors do NOT stop polling
 */

const SearchContext = createContext(null);

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;

const POLL_INTERVAL_MS = 3000;

export const SearchProvider = ({ children }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeSearchId, setActiveSearchId] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [progress, setProgress] = useState('');
  // Last terminal transition observed in this session: { id, status, leadsCount, errorMessage }.
  // The Dashboard reads it to load the results of a search that finished while the user was
  // on another page. Never cleared by navigation — only by starting a new search or logging out.
  const [finishedSearch, setFinishedSearch] = useState(null);

  // Search ids already announced. A ref (not state) so it can never trigger a re-render and
  // never resets on navigation — this is what guarantees one notification per search.
  const notifiedRef = useRef(new Set());
  // Current route, read inside the poll callback without making it a dependency (a changing
  // dependency would tear down and restart the polling interval on every navigation).
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);
  // One-shot adoption of an already-running search per session.
  const adoptedRef = useRef(false);

  const announce = useCallback((id, status, leadsCount, errorMessage) => {
    if (notifiedRef.current.has(id)) return;
    notifiedRef.current.add(id);

    // On the Dashboard the results render inline — keep exactly today's transient toast and
    // do NOT raise a persistent notification.
    if (pathnameRef.current === '/dashboard') {
      if (status === 'completed') {
        toast.success(`Found ${leadsCount || 0} leads`);
      } else {
        toast.error(errorMessage || 'Search failed. Please try again.');
      }
      return;
    }

    // Anywhere else: a persistent notification the user dismisses themselves.
    if (status === 'completed') {
      toast.success('Search complete', {
        description: `${leadsCount || 0} leads are ready to review.`,
        duration: Infinity,
        closeButton: true,
        action: {
          label: 'View results',
          onClick: () => navigate(`/dashboard?search=${id}`),
        },
      });
    } else {
      // No action button: a failed search has no leads, so "view" would open an empty page.
      toast.error('Search failed', {
        description: errorMessage || 'The search did not complete. Please try again.',
        duration: Infinity,
        closeButton: true,
      });
    }
  }, [navigate]);

  // One poll tick. Returns true when the search reached a terminal state.
  const pollSearchStatus = useCallback(async (searchId) => {
    if (!searchId || !token) return false;

    try {
      const response = await axios.get(
        `${API_URL}/searches/${searchId}/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { status, progress: progressText, leads_count, error_message } = response.data;

      if (progressText) {
        setProgress(progressText);
      }

      if (status === 'completed' || status === 'failed') {
        setIsSearching(false);
        setActiveSearchId(null);
        setProgress('');
        setFinishedSearch({
          id: searchId,
          status,
          leadsCount: leads_count,
          errorMessage: error_message,
        });
        announce(searchId, status, leads_count, error_message);
        return true; // Stop polling
      }

      return false; // Continue polling
    } catch (error) {
      console.error('Status poll error:', error);
      return false; // Continue polling on error
    }
  }, [token, announce]);

  // Polling effect — the interval is always cleared on unmount, logout or id change.
  useEffect(() => {
    if (!activeSearchId || !isSearching || !token) return;

    const pollInterval = setInterval(async () => {
      const done = await pollSearchStatus(activeSearchId);
      if (done) {
        clearInterval(pollInterval);
      }
    }, POLL_INTERVAL_MS);

    // Initial poll
    pollSearchStatus(activeSearchId);

    return () => clearInterval(pollInterval);
  }, [activeSearchId, isSearching, token, pollSearchStatus]);

  // Adopt a search that is already running when the app loads (e.g. after a full reload, or
  // when the tab was closed mid-search). Same logic as the previous Dashboard-local version,
  // including "newest wins" — /searches is sorted created_at desc. This is a ONE-TIME fetch on
  // provider mount, never a poll.
  useEffect(() => {
    if (!token || adoptedRef.current) return;
    adoptedRef.current = true;

    axios.get(`${API_URL}/searches`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(response => {
        const runningSearch = response.data.find(s => s.status === 'running');
        if (runningSearch) {
          setActiveSearchId(runningSearch.id);
          setIsSearching(true);
          setProgress('Resuming search...');
          toast.info('Resuming in-progress search...');
        }
      })
      .catch(() => {});
  }, [token]);

  // Logout / token loss: drop everything so nothing leaks into the next session.
  useEffect(() => {
    if (token) return;
    setActiveSearchId(null);
    setIsSearching(false);
    setProgress('');
    setFinishedSearch(null);
    notifiedRef.current = new Set();
    adoptedRef.current = false;
  }, [token]);

  // Called by the Dashboard once POST /searches has returned the new search id.
  const startSearch = useCallback((searchId) => {
    setFinishedSearch(null);
    setActiveSearchId(searchId);
    setIsSearching(true);
    setProgress('Initializing search...');
  }, []);

  // Called when the user deliberately dismisses the finished search (the "New Search" button),
  // so the Dashboard shows an empty form instead of immediately re-loading those results.
  const clearFinishedSearch = useCallback(() => {
    setFinishedSearch(null);
  }, []);

  const value = {
    activeSearchId,
    isSearching,
    progress,
    finishedSearch,
    startSearch,
    clearFinishedSearch,
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchTracker = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearchTracker must be used within a SearchProvider');
  }
  return context;
};
